import { useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail, BACKEND_ORIGIN } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Building2, Upload, Trash2, Save, Palette } from "lucide-react";

const COMPANY_FIELDS = [
  ["name", "Legal name (PDF header)"], ["brand", "Brand wordmark"], ["tagline", "Tagline (e.g. CONCRETE)"],
  ["abn", "ABN / Tax ID"], ["phone", "Phone"], ["email", "Accounts email"], ["website", "Website"],
  ["address_1", "Plant address"], ["address_2", "Mailing address"], ["bank", "Bank"], ["bsb", "BSB"], ["acct", "Account No."],
];

export default function OrgBrandingCard() {
  const { org, refreshOrg } = useAuth();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (org && !form) setForm({ name: org.name || "", brand_primary: org.brand_primary || "#F57C00", brand_accent: org.brand_accent || "#18181B", dispatch_default_view: org.dispatch_default_view || "classic", company: { ...(org.company || {}) } });
  }, [org, form]);

  if (!form) return null;

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/org/me", form);
      await refreshOrg();
      toast.success("Organization branding saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post("/org/me/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await refreshOrg();
      toast.success("Logo uploaded — it now appears in the app header and on every PDF");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const removeLogo = async () => {
    await api.delete("/org/me/logo");
    await refreshOrg();
    toast.success("Logo removed");
  };

  const logoSrc = org?.logo_url ? (org.logo_url.startsWith("/api") ? `${BACKEND_ORIGIN}${org.logo_url}` : org.logo_url) : null;

  return (
    <div className="border border-industrial bg-surface" data-testid="org-branding-card">
      <div className="px-5 py-4 border-b border-industrial flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Building2 className="w-4 h-4 text-primary-orange" />
          <div>
            <div className="label-tag">ORGANIZATION</div>
            <h2 className="heading font-black uppercase text-base md:text-lg">Branding & Company Details</h2>
          </div>
        </div>
        <span className="mono text-[10px] text-muted-industrial" data-testid="org-id-label">{org?.id} · {org?.plan}</span>
      </div>

      <div className="p-5 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="space-y-4">
          <div>
            <Label className="label-tag">Organization name</Label>
            <Input data-testid="org-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-none bg-transparent border-industrial" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            {[["brand_primary", "Primary colour"], ["brand_accent", "Accent colour"]].map(([k, lbl]) => (
              <div key={k}>
                <Label className="label-tag flex items-center gap-1"><Palette className="w-3 h-3" />{lbl}</Label>
                <div className="flex items-center gap-2">
                  <input type="color" data-testid={`${k}-picker`} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value.toUpperCase() })} className="h-9 w-10 bg-transparent border border-industrial cursor-pointer" />
                  <Input data-testid={`${k}-input`} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="rounded-none bg-transparent border-industrial mono text-xs" />
                </div>
              </div>
            ))}
          </div>
          <div>
            <Label className="label-tag">Default dispatch view</Label>
            <div className="flex gap-1 mt-1">
              {[["classic", "Classic Dispatch"], ["war_room", "War Room"]].map(([v, l]) => (
                <button key={v} type="button" data-testid={`default-view-${v}`} onClick={() => setForm({ ...form, dispatch_default_view: v })}
                  className={`mono text-[10px] uppercase tracking-wider px-3 py-1.5 border ${form.dispatch_default_view === v ? "border-primary-orange text-primary-orange" : "border-industrial text-muted-industrial"}`}>{l}</button>
              ))}
            </div>
          </div>
          <div>
            <Label className="label-tag">Logo (PNG / JPG, max 2 MB)</Label>
            <div className="mt-1 border border-dashed border-industrial p-3 flex items-center gap-3">
              <div className="h-14 w-32 flex items-center justify-center bg-white/5 border border-industrial overflow-hidden">
                {logoSrc ? <img src={logoSrc} alt="logo" className="max-h-12 max-w-full object-contain" data-testid="org-logo-preview" /> : <span className="mono text-[10px] text-muted-industrial">NO LOGO</span>}
              </div>
              <div className="flex flex-col gap-1">
                <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" data-testid="org-logo-file" onChange={(e) => upload(e.target.files?.[0])} />
                <Button type="button" data-testid="org-logo-upload-btn" variant="outline" className="rounded-none h-8 text-xs" onClick={() => fileRef.current?.click()}><Upload className="w-3.5 h-3.5 mr-1" />Upload</Button>
                {org?.logo_url && <Button type="button" data-testid="org-logo-remove-btn" variant="ghost" className="rounded-none h-8 text-xs text-red-400" onClick={removeLogo}><Trash2 className="w-3.5 h-3.5 mr-1" />Remove</Button>}
              </div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-3">
          {COMPANY_FIELDS.map(([k, lbl]) => (
            <div key={k}>
              <Label className="label-tag">{lbl}</Label>
              <Input data-testid={`company-${k}-input`} value={form.company[k] || ""} onChange={(e) => setForm({ ...form, company: { ...form.company, [k]: e.target.value } })} className="rounded-none bg-transparent border-industrial text-sm" />
            </div>
          ))}
          <div className="md:col-span-2 flex justify-end">
            <Button data-testid="org-save-btn" onClick={save} disabled={saving} className="rounded-none bg-primary-orange text-white hover:opacity-90"><Save className="w-4 h-4 mr-2" />{saving ? "Saving…" : "Save branding"}</Button>
          </div>
        </div>
      </div>
    </div>
  );
}
